const escapeAttr = (value: string): string => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
};

/** Build a CSS selector for a `data-tour` anchor. */
export const tourSelector = (target: string): string =>
  `[data-tour="${escapeAttr(target)}"]`;

/** True when an anchor has a painted box and is not display/visibility-hidden. */
export const isTourTargetVisible = (el: HTMLElement): boolean => {
  if (typeof window === 'undefined') return true;
  try {
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (Number(style.opacity) === 0) return false;
    const rects = el.getClientRects();
    if (!rects.length) return false;
    const r = rects[0];
    return r.width > 0 && r.height > 0;
  } catch {
    return true;
  }
};

/** Find the first visible matching tour target in the document. */
export const findTourTarget = (
  target: string,
  root: ParentNode = document
): HTMLElement | null => {
  const pick = (list: NodeListOf<Element> | Element[]): HTMLElement | null => {
    const nodes = Array.from(list) as HTMLElement[];
    const visible = nodes.find(isTourTargetVisible);
    return visible ?? nodes[0] ?? null;
  };

  try {
    return pick(root.querySelectorAll(tourSelector(target)));
  } catch {
    return pick(document.querySelectorAll(`[data-tour="${target}"]`));
  }
};

/** Wait briefly for a target that may mount after route paint. */
export const waitForTourTarget = (
  target: string,
  opts: { timeoutMs?: number; intervalMs?: number } = {}
): Promise<HTMLElement | null> => {
  const timeoutMs = opts.timeoutMs ?? 1200;
  const intervalMs = opts.intervalMs ?? 80;
  const existing = findTourTarget(target);
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const el = findTourTarget(target);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(null);
        return;
      }
      window.setTimeout(tick, intervalMs);
    };
    tick();
  });
};

/** Scroll a target into a readable band, then wait for layout to settle. */
export const scrollTourTargetIntoView = (
  el: HTMLElement,
  opts: { reduceMotion?: boolean } = {}
): Promise<void> =>
  new Promise((resolve) => {
    const stickyOffset = 72; // sticky top bar
    const rect = el.getBoundingClientRect();
    const viewH = window.innerHeight;
    const idealTop = stickyOffset + Math.min(120, viewH * 0.12);
    const alreadyReadable =
      rect.top >= stickyOffset + 8 &&
      rect.top <= viewH * 0.45 &&
      rect.bottom > stickyOffset + 48;

    if (alreadyReadable) {
      resolve();
      return;
    }

    const absoluteTop = window.scrollY + rect.top;
    const nextY = Math.max(0, absoluteTop - idealTop);
    window.scrollTo({
      top: nextY,
      behavior: opts.reduceMotion ? 'auto' : 'smooth',
    });

    // Remeasure after scroll settles (smooth scroll has no end event everywhere).
    const started = performance.now();
    let lastY = window.scrollY;
    const tick = () => {
      const y = window.scrollY;
      const settled = Math.abs(y - lastY) < 1;
      lastY = y;
      if (settled && performance.now() - started > 80) {
        resolve();
        return;
      }
      if (performance.now() - started > 900) {
        resolve();
        return;
      }
      window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });
